'use client';

import React, { useState, useMemo } from 'react';
import dynamic from 'next/dynamic';
import {
  MapPin,
  Search,
  X,
  Flame,
  Check,
  Map,
  Grid,
  ExternalLink,
  Zap,
  Crown,
} from 'lucide-react';
import {
  type MockGame,
  sportFilters,
  formatDistance,
  formatTime,
  getTimeUntil,
} from '@/lib/mock-data';
import { SportCardBanner } from './SportHeroArt';
import { getGoogleMapsUrl } from '@/lib/maps';
import { ElasticSearchEngine } from '@/lib/search-engine';
import { isUserHostOfGame, isUserPlayerInGame } from '@/pages/Home';

const GameMap = dynamic(() => import('@/components/GameMap'), { ssr: false });

interface FindGamesPageProps {
  games: MockGame[];
  liveCounters: Map<string, number>;
  joinedGames: Set<string>;
  authUser: any;
  userLocation: { lat: number; lng: number; name: string } | null;
  onSelectGame: (game: MockGame) => void;
  onJoinGame: (game: MockGame) => void;
  onRequireAuth: () => void;
  onRequestLocation: () => void;
}

export default function FindGamesPage({
  games,
  liveCounters,
  joinedGames,
  authUser,
  userLocation,
  onSelectGame,
  onJoinGame,
  onRequireAuth,
  onRequestLocation,
}: FindGamesPageProps) {
  const [activeSport, setActiveSport] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [maxDistanceKm, setMaxDistanceKm] = useState<number>(25);
  const [showOnlyNeedOne, setShowOnlyNeedOne] = useState(false);
  const [viewMode, setViewMode] = useState<'split' | 'grid'>('split');
  const [activeGame, setActiveGame] = useState<MockGame | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const searchEngine = useMemo(() => {
    return new ElasticSearchEngine(games);
  }, [games]);

  const filteredGames = useMemo(() => {
    const { rankedGames } = searchEngine.search(searchQuery, activeSport);
    return rankedGames.filter((g) => {
      const distanceKm = (g.distance || 0) / 1000;
      if (distanceKm > maxDistanceKm) return false;
      const count = liveCounters.get(g.id) ?? g.confirmedPlayers;
      const needed = g.capacity - count;
      if (showOnlyNeedOne && needed !== 1) return false;
      return true;
    });
  }, [searchEngine, activeSport, maxDistanceKm, showOnlyNeedOne, searchQuery, liveCounters]);

  return (
    <div className="find-games-container" style={{ padding: '24px 20px', maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', background: '#10B981', boxShadow: '0 0 10px #10B981' }}></span>
            <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#10B981' }}>
              Live Match Radar
            </span>
          </div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '28px', fontWeight: 800, letterSpacing: '-0.02em', margin: 0 }}>
            Find Games Near You
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: '4px 0 0' }}>
            Discover casual & competitive pickups within walking and driving distance.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={onRequestLocation}
            className="location-badge"
            style={{
              cursor: 'pointer',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              padding: '8px 14px',
              borderRadius: '999px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--text)',
            }}
            title="Click to detect or calibrate your GPS location"
          >
            <MapPin size={14} color="#3B82F6" />
            <span>{userLocation?.name || 'Current Location'}</span>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Auto</span>
          </button>

          <div style={{ display: 'flex', background: 'var(--elevated)', borderRadius: '8px', padding: '2px', border: '1px solid var(--border)' }}>
            <button
              onClick={() => setViewMode('split')}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: viewMode === 'split' ? 'var(--surface)' : 'transparent',
                color: viewMode === 'split' ? 'var(--text)' : 'var(--text-muted)',
                boxShadow: viewMode === 'split' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
              }}
            >
              <Map size={13} />
              <span>Map + List</span>
            </button>
            <button
              onClick={() => setViewMode('grid')}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: viewMode === 'grid' ? 'var(--surface)' : 'transparent',
                color: viewMode === 'grid' ? 'var(--text)' : 'var(--text-muted)',
                boxShadow: viewMode === 'grid' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
              }}
            >
              <Grid size={13} />
              <span>Cards Only</span>
            </button>
          </div>
        </div>
      </div>

      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '16px', padding: '16px', marginBottom: '24px', boxShadow: 'var(--shadow-sm)' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '14px' }}>
          <div style={{ flex: '1', minWidth: '260px', position: 'relative' }}>
            <input
              type="text"
              placeholder="Search by neighborhood, ground, or sport..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 14px 10px 36px',
                borderRadius: '10px',
                border: '1px solid var(--border)',
                background: 'var(--elevated)',
                color: 'var(--text)',
                fontSize: '14px',
                outline: 'none',
              }}
            />
            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', opacity: 0.5, display: 'flex', alignItems: 'center' }}>
              <Search size={14} />
            </span>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  color: 'var(--text-muted)',
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: 600 }}>Radius:</span>
            {[2, 5, 10, 25, 50].map((km) => (
              <button
                key={km}
                onClick={() => setMaxDistanceKm(km)}
                style={{
                  padding: '6px 10px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: maxDistanceKm === km ? 'var(--primary)' : 'var(--border)',
                  background: maxDistanceKm === km ? 'var(--primary)' : 'var(--elevated)',
                  color: maxDistanceKm === km ? '#fff' : 'var(--text)',
                  transition: 'all 0.15s ease',
                }}
              >
                {km} km
              </button>
            ))}
          </div>

          <button
            onClick={() => setShowOnlyNeedOne(!showOnlyNeedOne)}
            style={{
              padding: '6px 12px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
              border: '1px solid',
              borderColor: showOnlyNeedOne ? '#EF4444' : 'var(--border)',
              background: showOnlyNeedOne ? 'rgba(239, 68, 68, 0.15)' : 'var(--elevated)',
              color: showOnlyNeedOne ? '#EF4444' : 'var(--text)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <Flame size={14} color={showOnlyNeedOne ? '#EF4444' : 'currentColor'} />
            <span>Need 1 Player Only</span>
          </button>
        </div>

        {searchQuery.trim() && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            background: 'rgba(59, 130, 246, 0.08)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '8px',
            marginBottom: '12px',
          }}>
            <span style={{ fontSize: '12px', color: '#60A5FA', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <Zap size={13} style={{ flexShrink: 0 }} /> BM25 Search: {filteredGames.length} result{filteredGames.length !== 1 ? 's' : ''} for &quot;{searchQuery}&quot;
            </span>
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Clear
            </button>
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
          {sportFilters.map((sf) => (
            <button
              key={sf.key}
              onClick={() => setActiveSport(sf.key)}
              className={`sport-chip ${activeSport === sf.key ? 'active' : ''}`}
              style={{
                padding: '6px 14px',
                borderRadius: '999px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {sf.label} ({sf.count})
            </button>
          ))}
        </div>
      </div>

      {viewMode === 'split' ? (
        <div className="find-games-split-layout">
          <div className="find-games-map-side">
            <GameMap
              games={filteredGames}
              liveCounters={liveCounters}
              onGameClick={(g) => {
                setActiveGame(g);
                setIsSheetOpen(true);
              }}
              userCoords={userLocation ? { lat: userLocation.lat, lng: userLocation.lng } : undefined}
              userLocationName={userLocation?.name || 'Your Location'}
            />
          </div>

          <div className="find-games-cards-side">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text)' }}>
                {filteredGames.length} Game{filteredGames.length !== 1 ? 's' : ''} Available
              </span>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                Sorted by distance from you
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {filteredGames.map((game) => (
                <GameRadarCard
                  key={game.id}
                  game={game}
                  liveCount={liveCounters.get(game.id) ?? game.confirmedPlayers}
                  isJoined={Boolean(authUser && joinedGames.has(game.id))}
                  onSelect={() => {
                    setActiveGame(game);
                    setIsSheetOpen(true);
                    onSelectGame(game);
                  }}
                  onJoin={() => {
                    if (!authUser) {
                      onRequireAuth();
                      return;
                    }
                    onJoinGame(game);
                  }}
                />
              ))}

              {filteredGames.length === 0 && (
                <div style={{ textAlign: 'center', padding: '48px 24px', background: 'var(--surface)', borderRadius: '16px', border: '1px solid var(--border)' }}>
                  <div style={{ width: '48px', height: '48px', margin: '0 auto 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                    <Search size={36} />
                  </div>
                  <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '6px' }}>No games found in this radius</h3>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '16px' }}>
                    Try expanding your search distance or switching sports to find open slots.
                  </p>
                  <button
                    onClick={() => {
                      setMaxDistanceKm(50);
                      setActiveSport('ALL');
                      setSearchQuery('');
                    }}
                    className="join-btn"
                    style={{ padding: '8px 18px', fontSize: '13px' }}
                  >
                    Reset Filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text)' }}>
              {filteredGames.length} Game{filteredGames.length !== 1 ? 's' : ''} Found
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
            {filteredGames.map((game) => (
              <GameRadarCard
                key={game.id}
                game={game}
                liveCount={liveCounters.get(game.id) ?? game.confirmedPlayers}
                isJoined={Boolean(authUser && (joinedGames.has(game.id) || isUserPlayerInGame(game, authUser, joinedGames)))}
                isHost={Boolean(authUser && isUserHostOfGame(game, authUser))}
                onSelect={() => onSelectGame(game)}
                onJoin={() => {
                  if (!authUser) {
                    onRequireAuth();
                    return;
                  }
                  onJoinGame(game);
                }}
              />
            ))}
          </div>
        </div>
      )}

      {activeGame && (
        <div className={`mobile-game-sheet-overlay ${isSheetOpen ? 'open' : ''}`} onClick={() => setIsSheetOpen(false)}>
          <div className="mobile-game-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="sheet-handle-bar" onClick={() => setIsSheetOpen(!isSheetOpen)}>
              <div className="sheet-handle"></div>
            </div>

            <div className="sheet-content">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Match Preview
                </span>
                <button
                  onClick={() => setIsSheetOpen(false)}
                  style={{
                    background: 'var(--elevated)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '28px',
                    height: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: 'var(--text-secondary)',
                  }}
                  aria-label="Close card preview"
                >
                  <X size={14} />
                </button>
              </div>

              <GameRadarCard
                game={activeGame}
                liveCount={liveCounters.get(activeGame.id) ?? activeGame.confirmedPlayers}
                isJoined={Boolean(authUser && (joinedGames.has(activeGame.id) || isUserPlayerInGame(activeGame, authUser, joinedGames)))}
                isHost={Boolean(authUser && isUserHostOfGame(activeGame, authUser))}
                onSelect={() => onSelectGame(activeGame)}
                onJoin={() => {
                  if (!authUser) {
                    onRequireAuth();
                    return;
                  }
                  onJoinGame(activeGame);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function GameRadarCard({
  game,
  liveCount,
  isJoined,
  isHost,
  onSelect,
  onJoin,
}: {
  key?: React.Key;
  game: MockGame;
  liveCount: number;
  isJoined: boolean;
  isHost?: boolean;
  onSelect: () => void;
  onJoin: () => void;
}) {
  const needed = game.capacity - liveCount;
  const isNeedOne = needed === 1;
  const isFull = needed <= 0;
  const fillPercent = Math.min((liveCount / game.capacity) * 100, 100);

  return (
    <div
      onClick={onSelect}
      style={{
        background: 'var(--surface)',
        borderRadius: '16px',
        border: isNeedOne ? '1.5px solid #EF4444' : '1px solid var(--border)',
        overflow: 'hidden',
        cursor: 'pointer',
        boxShadow: isNeedOne ? '0 4px 16px rgba(239, 68, 68, 0.15)' : 'var(--shadow-sm)',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
      }}
    >
      <SportCardBanner
        sport={game.sport}
        isLive={game.isLive}
        isNeedOne={isNeedOne}
        timeUntil={getTimeUntil(game.startsAt)}
      />

      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 4px', color: 'var(--text)' }}>
              {game.title}
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <MapPin size={13} color="var(--primary)" />
                {game.playground.name}
              </span>
              <span>•</span>
              <span style={{ fontWeight: 600, color: 'var(--primary)' }}>{formatDistance(game.distance)} away</span>
            </div>
          </div>

          <a
            href={getGoogleMapsUrl(game.playground)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{
              padding: '4px 8px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: 700,
              background: 'rgba(124, 58, 237, 0.1)',
              color: 'var(--primary)',
              textDecoration: 'none',
              border: '1px solid rgba(124, 58, 237, 0.25)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <span>Maps</span>
            <ExternalLink size={11} />
          </a>
        </div>

        <div style={{ margin: '12px 0 14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text)' }}>
              {liveCount} <span style={{ opacity: 0.5, fontWeight: 400 }}>/ {game.capacity} Players</span>
            </span>
            <span
              style={{
                fontSize: '11px',
                fontWeight: 800,
                color: isNeedOne ? '#EF4444' : isFull ? 'var(--text-muted)' : '#10B981',
                background: isNeedOne ? 'rgba(239,68,68,0.1)' : isFull ? 'var(--elevated)' : 'rgba(16,185,129,0.1)',
                padding: '2px 8px',
                borderRadius: '6px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              {isFull ? (
                'FULL'
              ) : isNeedOne ? (
                <>
                  <Flame size={12} /> NEED 1 MORE
                </>
              ) : (
                `NEED ${needed} MORE`
              )}
            </span>
          </div>

          <div style={{ height: '6px', width: '100%', background: 'var(--elevated)', borderRadius: '999px', overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                width: `${fillPercent}%`,
                background: isNeedOne ? '#EF4444' : 'var(--brand-gradient)',
                borderRadius: '999px',
                transition: 'width 0.4s ease',
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            Starts {formatTime(game.startsAt)}
          </div>

          {isHost ? (
            <span
              style={{
                padding: '6px 14px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 700,
                background: 'rgba(124, 58, 237, 0.12)',
                color: '#8B5CF6',
                border: '1px solid rgba(124, 58, 237, 0.3)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <Crown size={13} style={{ flexShrink: 0 }} /> Hosting
            </span>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onJoin();
              }}
              disabled={isJoined}
              className={`join-btn ${isFull && !isJoined ? 'waitlist' : ''} ${isJoined ? 'full' : ''}`}
              style={{
                padding: '6px 18px',
                fontSize: '12px',
                fontWeight: 700,
                background: isJoined ? '#10B981' : undefined,
                borderColor: isJoined ? '#10B981' : undefined,
                color: isJoined ? '#fff' : undefined,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              {isJoined ? (
                <>
                  <Check size={13} /> Joined
                </>
              ) : isFull ? (
                'Join Waitlist'
              ) : isNeedOne ? (
                'Fill Last Slot'
              ) : (
                'Join Game'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
